import { useEffect, useId, useRef, useState } from "react";
import { Calendar } from "lucide-react";
import clsx from "clsx";
import { BookingDateCalendar } from "@/components/ui/BookingDateCalendar";
import { clampDateToMin, formatIsoDateShort } from "@/lib/bookingDates";

type ActiveField = "arrival" | "departure";

export interface BookingPeriodDateFieldsProps {
  arrivalId: string;
  departureId: string;
  arrivalLabel?: string;
  departureLabel?: string;
  arrival: string;
  departure: string;
  arrivalMin: string;
  departureMin: string;
  onArrivalChange: (value: string) => void;
  onDepartureChange: (value: string) => void;
  required?: boolean;
  /** Felder in übergeordnetes Grid einbetten (z. B. neben Stellplatz/Status). */
  embedded?: boolean;
}

function DateDisplay({ value }: { value: string }) {
  return (
    <span
      className={clsx(
        "eh-date-field__display eh-input",
        !value && "text-label-tertiary",
      )}
    >
      <span className="min-w-0 flex-1 font-medium text-label">
        {value ? formatIsoDateShort(value) : "Datum wählen"}
      </span>
      <Calendar className="eh-date-field__icon" aria-hidden />
    </span>
  );
}

/**
 * Anreise- und Abreisedatum mit eigenem Kalender.
 * Der native Date-Picker öffnet sich in Dialogen nicht zuverlässig
 * (unsichtbares Overlay, Focus-Trap). Nach der Anreise springt der
 * Kalender direkt zur Abreise; die gewählte Anreise bleibt markiert.
 */
export function BookingPeriodDateFields({
  arrivalId,
  departureId,
  arrivalLabel = "Anreise *",
  departureLabel = "Abreise *",
  arrival,
  departure,
  arrivalMin,
  departureMin,
  onArrivalChange,
  onDepartureChange,
  required,
  embedded = false,
}: BookingPeriodDateFieldsProps) {
  const arrivalLabelId = useId();
  const departureLabelId = useId();
  const wrapperRef = useRef<HTMLDivElement>(null);
  const calendarRef = useRef<HTMLDivElement>(null);

  const [calendarOpen, setCalendarOpen] = useState(false);
  const [activeField, setActiveField] = useState<ActiveField>("arrival");

  const departureDisabled = !arrival;

  useEffect(() => {
    if (!calendarOpen) return;
    function onPointerDown(event: PointerEvent) {
      if (!wrapperRef.current?.contains(event.target as Node)) {
        setCalendarOpen(false);
      }
    }
    document.addEventListener("pointerdown", onPointerDown);
    return () => document.removeEventListener("pointerdown", onPointerDown);
  }, [calendarOpen]);

  useEffect(() => {
    if (!calendarOpen) return;
    calendarRef.current?.scrollIntoView({ block: "nearest" });
  }, [calendarOpen, activeField]);

  function applyArrival(next: string) {
    if (!next) {
      onArrivalChange("");
      return;
    }
    const iso = clampDateToMin(next, arrivalMin);
    onArrivalChange(iso);
    if (departure && departure <= iso) {
      onDepartureChange("");
    }
  }

  function applyDeparture(next: string) {
    if (!next) {
      onDepartureChange("");
      return;
    }
    onDepartureChange(clampDateToMin(next, departureMin));
  }

  function openCalendar(field: ActiveField) {
    if (field === "departure" && departureDisabled) return;
    if (calendarOpen && activeField === field) {
      setCalendarOpen(false);
      return;
    }
    setActiveField(field);
    setCalendarOpen(true);
  }

  function handleSelect(next: string) {
    if (activeField === "arrival") {
      applyArrival(next);
      setActiveField("departure");
      return;
    }
    applyDeparture(next);
    setCalendarOpen(false);
  }

  const calendarValue = activeField === "arrival" ? arrival : departure;
  const calendarMin = activeField === "arrival" ? arrivalMin : departureMin;
  const calendarLabelId = activeField === "arrival" ? arrivalLabelId : departureLabelId;

  const fields = (
    <>
      <label htmlFor={arrivalId} id={arrivalLabelId} className="block min-w-0 text-sm">
        <span className="mb-1 block text-label-secondary">{arrivalLabel}</span>
        <div
          className={clsx(
            "eh-date-field",
            calendarOpen && activeField === "arrival" && "eh-date-field--open",
          )}
        >
          <button
            id={arrivalId}
            type="button"
            className="eh-date-field__trigger"
            aria-haspopup="dialog"
            aria-expanded={calendarOpen && activeField === "arrival"}
            aria-required={required || undefined}
            aria-labelledby={arrivalLabelId}
            onClick={() => openCalendar("arrival")}
          >
            <DateDisplay value={arrival} />
          </button>
        </div>
      </label>

      <label htmlFor={departureId} id={departureLabelId} className="block min-w-0 text-sm">
        <span className="mb-1 block text-label-secondary">{departureLabel}</span>
        <div
          className={clsx(
            "eh-date-field",
            departureDisabled && "eh-date-field--disabled",
            calendarOpen && activeField === "departure" && "eh-date-field--open",
          )}
        >
          <button
            id={departureId}
            type="button"
            className="eh-date-field__trigger"
            disabled={departureDisabled}
            aria-haspopup="dialog"
            aria-expanded={calendarOpen && activeField === "departure"}
            aria-required={required || undefined}
            aria-labelledby={departureLabelId}
            onClick={() => openCalendar("departure")}
          >
            <DateDisplay value={departure} />
          </button>
        </div>
      </label>

      {calendarOpen ? (
        <div
          ref={calendarRef}
          className={clsx(
            embedded ? "col-span-full sm:col-span-2" : "sm:col-span-2",
            "flex",
            activeField === "departure" && "sm:justify-end",
          )}
        >
          <BookingDateCalendar
            value={calendarValue}
            min={calendarMin}
            anchorDate={activeField === "departure" ? arrival : undefined}
            labelledBy={calendarLabelId}
            onChange={handleSelect}
          />
        </div>
      ) : null}
    </>
  );

  const gridClass = embedded ? "contents" : "grid min-w-0 gap-3 sm:grid-cols-2";

  return (
    <div ref={wrapperRef} className={gridClass}>
      {fields}
    </div>
  );
}
